import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Lock, PlugZap, Trash2, X, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { QK_POORTWACHTER } from './PoortwachterSigningWrapper';
import { getUwvConnector, slaUwvConnectorOp, verwijderUwvConnector, type UwvConnectorInstellingen } from '../services/uwvConnectorApi';

/**
 * UWV-connector per werkgever (besluit 11) — knop + instelscherm bij de
 * Poortwachter-tabel. Alleen zichtbaar voor de hoofdgebruiker of een teamlid
 * met de admin-rol. Het geheim wordt nooit getoond: de server meldt alleen of
 * het is ingesteld. Leeg laten bij opslaan = huidig geheim behouden.
 */

const QK_UWV_CONNECTOR = ['uwv-connector'];
const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback;
const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';
const isHttps = (url: string) => {
  try {
    return new URL(url.trim()).protocol === 'https:';
  } catch {
    return false;
  }
};

const JaNee: React.FC<{ ja: boolean; label: string }> = ({ ja, label }) => {
  const { t } = useTranslation('uwvConnector');
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-700">
      {ja ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <XCircle className="w-3.5 h-3.5 text-gray-400" />}
      <span>{label}: <strong>{ja ? t('ja') : t('nee')}</strong></span>
    </div>
  );
};

const UwvConnectorModal: React.FC<{ inst: UwvConnectorInstellingen; onSluit: () => void }> = ({ inst, onSluit }) => {
  const { t } = useTranslation('uwvConnector');
  const qc = useQueryClient();
  const [url, setUrl] = useState(inst.webhook_url ?? '');
  const [geheim, setGeheim] = useState('');
  const [actief, setActief] = useState(inst.actief ?? true);
  useEffect(() => {
    setUrl(inst.webhook_url ?? '');
    setActief(inst.webhook_url ? !!inst.actief : true);
  }, [inst.webhook_url, inst.actief]);

  const naWijziging = (data: UwvConnectorInstellingen) => {
    qc.setQueryData(QK_UWV_CONNECTOR, data);
    // Het meldingsvenster leest opnieuw of het kanaal 'koppeling' beschikbaar is
    qc.invalidateQueries({ queryKey: [...QK_POORTWACHTER, 'uwv'] });
    setGeheim('');
  };
  const opslaan = useMutation({
    mutationFn: () => slaUwvConnectorOp({ webhook_url: url.trim(), geheim: geheim.trim() || null, actief }),
    onSuccess: (data) => {
      naWijziging(data);
      toast.success(t('toastOpgeslagen'));
    },
    onError: (e: any) => toast.error(apiMessage(e, t('foutOpslaan'))),
  });
  const verwijder = useMutation({
    mutationFn: verwijderUwvConnector,
    onSuccess: (data) => {
      naWijziging(data);
      toast.success(t('toastVerwijderd'));
    },
    onError: (e: any) => toast.error(apiMessage(e, t('foutVerwijderen'))),
  });

  const urlGeldig = isHttps(url);
  const geheimTeKort = geheim.trim().length > 0 && geheim.trim().length < 16;
  const kanOpslaan = urlGeldig && !geheimTeKort && (inst.geheim_ingesteld || geheim.trim().length >= 16) && !opslaan.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" role="dialog" aria-modal="true" aria-label={t('titel')}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mt-10">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><PlugZap className="w-4 h-4 text-blue-600" /> {t('titel')}</h3>
          <button type="button" onClick={onSluit} aria-label={t('sluiten')} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700">{t('uitleg')}</p>

          <div className="space-y-1 p-3 rounded-lg bg-gray-50 border border-gray-200">
            <JaNee ja={!!inst.webhook_url} label={t('statusEigen')} />
            <JaNee ja={!!inst.geheim_ingesteld} label={t('statusGeheim')} />
            <JaNee ja={!!inst.koppeling_beschikbaar} label={t('statusBeschikbaar')} />
            {!inst.webhook_url && inst.platform_terugval && <p className="text-[11px] text-gray-500 pt-1">{t('terugvalActief')}</p>}
          </div>

          <div>
            <label className={labelClass} htmlFor="uwv-connector-url">{t('urlLabel')}</label>
            <input id="uwv-connector-url" className={inputClass} value={url} maxLength={500} placeholder="https://connector.voorbeeld.nl/uwv" onChange={(e) => setUrl(e.target.value)} />
            {url.trim() && !urlGeldig && <p className="text-[11px] text-red-700 mt-1">{t('alleenHttps')}</p>}
          </div>

          <div>
            <label className={labelClass} htmlFor="uwv-connector-geheim">{t('geheimLabel')}</label>
            <input
              id="uwv-connector-geheim"
              type="password"
              autoComplete="new-password"
              className={inputClass}
              value={geheim}
              maxLength={256}
              placeholder={inst.geheim_ingesteld ? t('geheimBehouden') : t('geheimNieuw')}
              onChange={(e) => setGeheim(e.target.value)}
            />
            {geheimTeKort && <p className="text-[11px] text-red-700 mt-1">{t('geheimTeKort')}</p>}
            <p className="text-[11px] text-gray-500 mt-1 flex items-start gap-1"><Lock className="w-3 h-3 mt-0.5 flex-shrink-0" /> {t('geheimPrivacy')}</p>
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
            <input type="checkbox" className="mt-1" checked={actief} onChange={(e) => setActief(e.target.checked)} />
            <span>{t('actiefLabel')}</span>
          </label>

          <div className="flex items-center justify-between gap-3 pt-3 border-t">
            {inst.webhook_url ? (
              <Button size="sm" variant="danger" disabled={verwijder.isPending} onClick={() => window.confirm(t('verwijderBevestig')) && verwijder.mutate()}>
                {verwijder.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5 mr-1" /> {t('verwijderen')}</>}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onSluit}>{t('sluiten')}</Button>
              <Button size="sm" disabled={!kanOpslaan} onClick={() => opslaan.mutate()}>
                {opslaan.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('opslaan')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Knop bij de Poortwachter-tabel; rendert niets voor teamleden zonder admin-rol. */
export const UwvConnectorKnop: React.FC = () => {
  const { t } = useTranslation('uwvConnector');
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: QK_UWV_CONNECTOR, queryFn: getUwvConnector, retry: false });
  if (!data?.mag_beheren) return null;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} title={t('knopTitel')}>
        <PlugZap className="w-4 h-4 mr-1" /> {t('knop')}
        <span className={`ml-2 inline-block w-2 h-2 rounded-full ${data.koppeling_beschikbaar ? 'bg-green-500' : 'bg-gray-300'}`} aria-hidden="true" />
      </Button>
      {open && <UwvConnectorModal inst={data} onSluit={() => setOpen(false)} />}
    </>
  );
};

export default UwvConnectorKnop;
